import { useEffect } from "react";
import { useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import { NEW_MUSICONHOLD } from "@/constants/New";
import Avatar from "@/design/components/Avatar";
import DeleteFlow from "@/design/components/DeleteFlow";
import HeaderCard from "@/design/components/HeaderCard";
import SaveIndicator from "@/design/components/SaveIndicator";
import FormGroup from "@/design/forms/FormGroup";
import InputText from "@/design/forms/InputText";
import InputToggle from "@/design/forms/InputToggle";
import { string } from "@/utils/hooks/endorse";
import useSave, { SaveState } from "@/utils/hooks/useSave";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viArrowLeft, viMusicPause } from "@sippy-platform/valkyrie";

import { move } from "@dnd-kit/helpers";
import { DragDropProvider } from "@dnd-kit/react";
import { Alert, Container, Grid, IconButton, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

import musicOnHoldReducer from "./musicOnHoldReducer";
import Sound from "./Sound";
import SoundDrawer from "./sound/SoundDrawer";
import SystemSound from "./SystemSound";

function MusicOnHold() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));

  const pbx = useSelector((state) => state.domain.pbx);
  const soundStore = useSelector((state) => state.domain.sounds);

  // Get general data
  const {
    value: musicOnHold,
    dispatch: musicOnHoldDispatch,
    setSaveState,
    formState,
    errors,
    allErrors,
    save,
  } = useSave({
    initialValue: NEW_MUSICONHOLD,
    reducer: musicOnHoldReducer,
    validator: () => ({
      name: string()
        .required({ message: "Name is required." })
        .length([1, 60], { message: "Name must be at most 60 characters." }),
    }),
    onSubmit: () => Api.storeMusicOnHold(musicOnHold),
    onSuccess: (response) => {
      if (!musicOnHold.id) {
        musicOnHoldDispatch({
          type: "id",
          value: response.data.musiconhold.id,
        });
        navigate(`../${response.data.musiconhold.id}`);
      }

      Promise.resolve(Api.getMusicOnHolds());
    },
  });

  useEffect(() => {
    let ignore = false;

    if (Number(id) === musicOnHold.id || !id) {
      return;
    }

    if (id === "new") {
      Api.cancelActiveGetRequests();
      musicOnHoldDispatch({ type: "set", value: NEW_MUSICONHOLD });

      return () => {
        ignore = true;
      };
    }

    toggleLoadingState(true);

    Promise.resolve(Api.getMusicOnHold({ id: Number(id) }))
      .then(
        (response) =>
          !ignore &&
          musicOnHoldDispatch({
            type: "set",
            value: response.data.musiconhold,
          }),
      )
      .finally(() => toggleLoadingState(false));

    return () => {
      ignore = true;
    };
  }, [id, musicOnHold.id, musicOnHoldDispatch]);

  // Handle changes
  const formHandler = (property, value) => {
    musicOnHoldDispatch({ type: property, value });
    setSaveState(SaveState.UNSAVED);
  };

  // Delete
  function onDeleteFinish() {
    Promise.resolve(Api.getMusicOnHolds()).then(() => navigate(".."));
  }

  return (
    <Container>
      <title>{`${musicOnHold.name || "Music on Hold"} \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Stack
        sx={{
          gap: 2,
        }}
      >
        <HeaderCard
          avatar={
            !matches ? (
              <IconButton variant="avatar" component={NavLink} to={".."}>
                <Valkyrie icon={viArrowLeft} /> <Avatar text={musicOnHold.name} icon={viMusicPause} />
              </IconButton>
            ) : (
              <Avatar text={musicOnHold.name} icon={viMusicPause} />
            )
          }
          action={
            <>
              <SaveIndicator formState={formState} errors={allErrors} save={save} />
              <DeleteFlow
                advanced
                key={musicOnHold.id || "nk"}
                isActionable={!!musicOnHold?.id && !musicOnHold.system}
                onDeleteCheck={() => Api.deleteMusicOnHold(musicOnHold, false)}
                onDelete={() => Api.deleteMusicOnHold(musicOnHold, true)}
                name={musicOnHold.name}
                onDeleteFinish={onDeleteFinish}
              />
            </>
          }
          title={musicOnHold.name || "Unnamed music on hold"}
        />
        {musicOnHold.system && (
          <Alert severity="info" sx={{ boxShadow: 1 }}>
            This is a system music on hold. You cannot change its contents.
          </Alert>
        )}
        <FormGroup title="Identity">
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputText
              required={!musicOnHold.system}
              disabled={musicOnHold.system}
              id="name"
              label="Name"
              onChange={formHandler}
              value={musicOnHold.name}
              error={errors}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 12, lg: 6 }}>
            <InputToggle
              disabled={musicOnHold.system}
              id="random"
              onChange={formHandler}
              states={[false, true]}
              value={musicOnHold.random}
              primary="Shuffle"
              secondary="Play the music in this list in a random order."
              error={errors}
            />
          </Grid>
        </FormGroup>
        <FormGroup
          title="Playlist"
          actions={
            !musicOnHold.system && <SoundDrawer musicOnHoldDispatch={musicOnHoldDispatch} musicOnHold={musicOnHold} />
          }
        >
          {musicOnHold.system ? (
            <Grid size={12}>
              <Stack
                sx={{
                  gap: 0.25,
                }}
              >
                {(musicOnHold.entries || []).map((entry) => (
                  <SystemSound entry={entry} key={entry.id} />
                ))}
              </Stack>
            </Grid>
          ) : (
            <Grid size={12}>
              <Stack
                sx={{
                  gap: 0.25,
                }}
              >
                <DragDropProvider
                  onDragEnd={(event) => {
                    musicOnHoldDispatch({ type: "moveSound", event });

                    const entries = move(musicOnHold.entries, event).map((entry, index) => ({
                      ...entry,
                      position: index + 1,
                    }));
                    const entry = entries.find((entry) => entry.id === event.operation.source.id);

                    Promise.resolve(Api.putMusicOnHoldEntry(musicOnHold, entry));
                  }}
                >
                  {(musicOnHold.entries || []).map((entry, index) => {
                    const sound = soundStore.list.find((_sound) => _sound.id === entry.soundId);
                    return (
                      <Sound
                        key={entry.id}
                        index={index}
                        entry={entry}
                        sound={sound}
                        id={entry.id}
                        musicOnHold={musicOnHold}
                        musicOnHoldDispatch={musicOnHoldDispatch}
                      />
                    );
                  })}
                </DragDropProvider>
                {!musicOnHold.system && musicOnHold.entries.length === 0 && (
                  <Alert severity={id !== "new" ? "error" : "info"}>
                    {id !== "new"
                      ? "This playlists has no sounds. It won't function as expected."
                      : "Add sounds to the playlist to make this music on hold work as expected."}
                  </Alert>
                )}
              </Stack>
            </Grid>
          )}
        </FormGroup>
      </Stack>
    </Container>
  );
}

export default MusicOnHold;
